import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { CgSoftwareDownload } from 'react-icons/cg';
import { Softlist } from './list';
import PageTitle from "../../components/PageTitle";
import './software.css';

const SoftwareDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const software = Softlist.find((s) => s.id === id);
	const [showThanks, setShowThanks] = useState(false);
	const [likeCount, setLikeCount] = useState(0);
	const [downloadCount, setDownloadCount] = useState(0);
	const [isMobileDevice, setIsMobileDevice] = useState(false);

	useEffect(() => {
		const checkDevice = () => {
			const ua = navigator.userAgent;
			setIsMobileDevice(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua));
		};
		checkDevice();
	}, []);

	// localStorage から隠しソフトの表示フラグを取得
	const showHidden = localStorage.getItem('show_hidden_software') === 'true';
	// 「データが存在しない」または「隠し設定(flags: "true")だが解放フラグが立っていない」場合は非表示判定にする
	const isVisible = software && ((software as any).flags !== "true" || showHidden);

	// 触覚フィードバック
	const triggerHaptic = (pattern: number | number[]) => {
		if (typeof window !== "undefined" && window.navigator && window.navigator.vibrate) {
			window.navigator.vibrate(pattern as any);
		}
	};

	// いいね / ダウンロード 数を取得
	useEffect(() => {
		const fetchCurrentLike = async () => {
			try {
				const res = await fetch(`/software_likes.php?t=${Date.now()}`);
				const data = await res.json();
				// PHP側でキー名を software_id に変える場合はここも合わせて修正
				const target = data.find((item: any) => (item.software_id || item.illust_id) === id);
				if (target) setLikeCount(parseInt(target.count));
			} catch (e) { console.error(e); }
		};

		const fetchDownloadCount = async () => {
			try {
				const res = await fetch(`/software_downloads.php?t=${Date.now()}`);
				const data = await res.json();
				const target = data.find((item: any) => (item.software_id || item.illust_id) === id);
				if (target) setDownloadCount(parseInt(target.count));
			} catch (e) { console.error(e); }
		};

		fetchCurrentLike();
		fetchDownloadCount();
	}, [id]);

	if (!isVisible) {
		return (
			<div>
				<div className="software-page-wrapper" />
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5 }}
				>
				<PageTitle title="Not Found" />
				<div className="software-detail-wrapper" style={{ justifyContent: 'center', textAlign: 'center' }}>
					<div>
						<h2 style={{ color: 'white' }}>該当するモノが見つかりませんでした。</h2>
						<p style={{ color: 'white' }}>リンクが間違っているか、削除されたかもしれません。</p>
					</div>
				</div>
				</motion.div>
			</div>
		);
	}

	const storageKey = `liked_${id}`;
	const isAlreadyLiked = localStorage.getItem(storageKey);

	const handleLike = async () => {
		const action = isAlreadyLiked ? 'minus' : 'plus';

		try {
			const res = await fetch(`/software_likes.php?t=${Date.now()}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ 
					id: id, 
					action: action
				})
			});

			if (res.ok) {
				// いいねを配列で保存する（Galleryとの同期用）
				const currentLikes = localStorage.getItem('my_likes');
				let likedList: string[] = currentLikes ? JSON.parse(currentLikes) : [];

				if (isAlreadyLiked) {
					// キャンセル処理
					setLikeCount(prev => Math.max(0, prev - 1));
					localStorage.removeItem(storageKey);
					triggerHaptic(15);

					if (id) {
						likedList = likedList.filter(likedId => likedId !== id);
					}
				} else {
					// いいね処理
					setLikeCount(prev => prev + 1);
					localStorage.setItem(storageKey, "true");
					triggerHaptic(500);

					if (id && !likedList.includes(id)) {
						likedList.push(id);
					}
				}
				localStorage.setItem('my_likes', JSON.stringify(likedList));
			}
		} catch (err) { 
			console.error("いいね操作に失敗しました:", err); 
			triggerHaptic([50, 50, 50]);
		}
	};

	const handleShare = () => {
		if (window.navigator && window.navigator.vibrate) {
			window.navigator.vibrate(15);
		}

		if (navigator.share) {
			navigator.share({
				title: software.title,
				text: software.copy,
				url: window.location.href,
			}).catch(() => {});
		} else {
			const url = window.location.href;
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(url)
					.then(() => alert("URLをコピーしました！"))
					.catch(() => fallbackCopy(url));
			} else {
				fallbackCopy(url);
			}
		}
	};

	const handleDownload = async () => {
		setShowThanks(true);
		try {
			await fetch(`/software_downloads.php?t=${Date.now()}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id: id })
			});
			setDownloadCount(prev => prev + 1);
		} catch (err) {
			console.error("ダウンロード数の更新に失敗しました:", err);
		}
	};

	const fallbackCopy = (text: string) => {
		const textArea = document.createElement('textarea');
		textArea.value = text;
		document.body.appendChild(textArea);
		textArea.select();
		document.execCommand('copy');
		document.body.removeChild(textArea);
		alert("URLをコピーしました！");
	};

	// タグクリック時の遷移処理
	const handleTagClick = (tag: string) => {
		// タグ経由での遷移時は特定のカードへのスクロール復元を無効化する
		sessionStorage.removeItem('software-scroll-target');
		navigate('/software', { state: { filterTag: tag } });
	};

	// モバイル環境かつ文字数が多い場合にフォントサイズを調整する
	const titleFontSize = isMobileDevice && software ? (
		software.title.length > 25 ? '0.8rem' :
		software.title.length > 20 ? '0.9rem' :
		software.title.length > 15 ? '1.05rem' :
		software.title.length > 10 ? '1.2rem' : 
		'1.3rem'
	) : undefined;

	return (
		<div>
			<div className="software-page-wrapper" />
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.8, ease: "easeOut" }}
			>
			<PageTitle title={software.title} />

			<div className="software-detail-wrapper">
				<div className="software-detail-left">
					{/* 画像をクリックしたら別タブで表示 */}
					<a href={software.img} target="_blank" rel="noreferrer" onClick={() => triggerHaptic(15)}>
						<img 
							src={software.img} 
							alt={software.title} 
							className="software-detail-img" 
							style={{ cursor: 'pointer' }} 
						/>
					</a>
				</div>

				<div className="software-detail-right">
					<div className="software-detail-header">
						<h1 
							className="software-detail-title" 
							style={{ fontSize: titleFontSize }}
						>
							{software.title}
						</h1>
						<div className="software-detail-meta">
							<p>Ver {software.ver}</p>
							<p>Update: {software.update}</p>
						</div>
					</div>

					<div className="software-action-area">
						<button 
							className={`software-like-button ${isAlreadyLiked ? 'liked' : ''}`} 
							onClick={handleLike}
						>
							{isAlreadyLiked ? '❤️' : '🤍'} {likeCount}
						</button>

						<button className="software-share-button" onClick={handleShare}>
							🔗 共有
						</button>
					</div>

					<p className="software-detail-copy">{software.copy}</p>

					<div className="software-detail-comment">
						{software.description}
					</div>

					

										<div style={{ textAlign: 'center' }}>
						{isMobileDevice ? (
							/* モバイル環境：リンクではなく無効化された div として表示 */
							<div 
								className="software-download-button disabled"
								style={{ 
									display: 'flex', 
									alignItems: 'center', 
									justifyContent: 'center', 
									gap: '8px',
									opacity: 0.4, 
									cursor: 'not-allowed', 
									pointerEvents: 'none', 
									filter: 'grayscale(1)'
								}}
							>
								<CgSoftwareDownload size={22} />
								<span>ダウンロード</span>
							</div>
						) : (
							/* PC環境：正規のダウンロード用アンカータグを表示 */
							<a 
								href={software.download} 
								className="software-download-button"
								style={{ 
									display: 'flex', 
									alignItems: 'center', 
									justifyContent: 'center', 
									gap: '8px'
								}}
								download 
								onClick={handleDownload}
							>
								<CgSoftwareDownload size={22} />
								<span>ダウンロード</span>
							</a>
						)}
						{isMobileDevice && (
							<p style={{ 
								color: '#ffcccc', 
								fontSize: '0.85rem', 
								marginTop: '-20px', 
								marginBottom: '30px',
								fontFamily: '"Zen Maru Gothic", sans-serif'
							}}>
								※パソコン環境でダウンロード出来ます。
							</p>
						)}
					</div>
					
					<div className="software-download-count">
						ダウンロードされた数: {downloadCount}
					</div>


					<div className="software-detail-tags">
						{software.tags.map((tag, index) => (
							<span 
								key={index} 
								className="software-detail-tag"
								onClick={() => handleTagClick(tag)}
								style={{ cursor: 'pointer' }}
							>
								#{tag}
							</span>
						))}
					</div>

				</div>
			</div>

			<AnimatePresence>
				{showThanks && (
					<motion.div 
						className="download-thanks-overlay"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={() => setShowThanks(false)}
					>
						<div className="thanks-arrow">↗</div>
						<div className="thanks-content">
							<p className="thanks-text">ダウンロードありがとうございます！</p>
							<p className="thanks-subtext">圧縮ファイルになっていますので、解凍して起動して下さい。</p>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			

			</motion.div>
		</div>
	);
};

export default SoftwareDetail;